import Check from "@mui/icons-material/Check";
import { Stack, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { TruncateTypography } from "components/TruncateTypography";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { WasteLinesUnifiedQuery } from "generated-graphql/graphql";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router";
import { WASTE_LINES_UNIFIED_QUERY } from "routes/Customer/Waste/ManifestLineItems/Preview/api";
import { formatNumber } from "util/formatNumber";
import { useWasteFacilityState } from "../WasteFacilityContext";

type WasteLineUnifiedRow = NonNullable<
  WasteLinesUnifiedQuery["wasteLinesUnified"]
>["items"][number];

export const ManifestLineItemsDataGrid = ({
  wasteStreamId,
}: {
  wasteStreamId: string;
}) => {
  const navigate = useNavigate();
  const { tenantId } = useParams<{ tenantId: string }>();
  const {
    wasteFacilityState: { deferredFilters },
  } = useWasteFacilityState();
  const { startDate, endDate } = deferredFilters;

  const shippedDateFilter = useMemo(() => {
    const currentYear = DateTime.now().year;

    const dateFilter =
      startDate && endDate ? `${startDate}..${endDate}` : `${currentYear}`;
    return `${dateFilter}`;
  }, [endDate, startDate]);

  const defaultSearch = useMemo(
    () =>
      `tenantId:${tenantId} wasteStreamId:${wasteStreamId} shippedDate:${shippedDateFilter}`,
    [shippedDateFilter, tenantId, wasteStreamId]
  );

  const columns: OmnisearchGridColDef<WasteLineUnifiedRow>[] = useMemo(
    () => [
      {
        field: "lineItem",
        headerName: "Line Item",
        minWidth: 100,
        flex: 2,
        renderCell: ({ row: { willNotReport }, value }) => {
          return (
            <Box>
              <TruncateTypography variant="body2">{value}</TruncateTypography>
              {willNotReport && (
                <Typography variant="caption" color="text.secondary">
                  Not Reportable
                </Typography>
              )}
            </Box>
          );
        },
      },
      {
        field: "profileNumber",
        headerName: "Profile #",
        flex: 1,
        renderCell: ({ value }) => {
          return (
            <TruncateTypography variant="body2">{value}</TruncateTypography>
          );
        },
      },
      {
        field: "manifestTrackingNumber",
        headerName: "Manifest #",
        flex: 1,
        renderCell: ({ value }) => {
          return (
            <TruncateTypography variant="body2">{value}</TruncateTypography>
          );
        },
      },
      {
        field: "vendorAmount",
        headerName: "Amount (Vendor)",
        filterKeyType: "number",
        minWidth: 80,
        flex: 1,
        valueGetter: (_, row) => {
          const amount = formatNumber(row.vendorAmount);
          return amount ? `${amount} lbs` : "";
        },
        renderCell: ({ value }) => {
          return (
            <TruncateTypography variant="body2">{value}</TruncateTypography>
          );
        },
      },
      {
        field: "eManifestAmount",
        headerName: "Amount (e‑Manifest)",
        filterKeyType: "number",
        minWidth: 80,
        flex: 1,
        valueGetter: (_, row) => {
          const amount = formatNumber(row.eManifestAmount);
          return amount ? `${amount} lbs` : "";
        },
        renderCell: ({ value }) => {
          return (
            <TruncateTypography variant="body2">{value}</TruncateTypography>
          );
        },
      },
      {
        field: "shippedDate",
        headerName: "Ship Date",
        filterKeyType: "date",
        flex: 1,
        valueGetter: (_, row) => {
          return getUtcDisplayDate(row.shippedDate, "med");
        },
        renderCell: ({ value }) => {
          return (
            <TruncateTypography variant="body2">{value}</TruncateTypography>
          );
        },
      },
      {
        field: "dotDescription",
        headerName: "DOT Description",
        flex: 1,
        renderCell: ({ value }) => {
          return (
            <TruncateTypography variant="body2">{value}</TruncateTypography>
          );
        },
      },
      {
        field: "managementMethodCode",
        headerName: "Management Method Code",
        flex: 1,
        renderCell: ({ value }) => {
          return (
            <TruncateTypography variant="body2">{value}</TruncateTypography>
          );
        },
      },
      {
        field: "vendor",
        headerName: "Vendor",
        flex: 1,
        renderCell: ({ value }) => {
          return (
            <TruncateTypography variant="body2">{value}</TruncateTypography>
          );
        },
      },
      {
        field: "destinationFacilityName",
        headerName: "TSDF",
        flex: 1,
        renderCell: ({ value }) => {
          return (
            <TruncateTypography variant="body2">{value}</TruncateTypography>
          );
        },
      },
      {
        field: "willNotReport",
        headerName: "Not Reportable",
        filterKeyType: "boolean",
        sortable: false,
        flex: 1,
        renderCell: ({ value }) => {
          if (value === true) {
            return <Check />;
          }
          return null;
        },
      },
    ],
    []
  );

  return (
    <Stack spacing={2}>
      <Typography variant="subtitle1">Manifest line items</Typography>
      <OmnisearchDataGrid
        withPadding={false}
        persistenceKey="waste-stream-details-manifest-line-items"
        columns={columns}
        dataQuery={WASTE_LINES_UNIFIED_QUERY}
        getItems={(data) => data.wasteLinesUnified?.items ?? []}
        getCount={(data) => data.wasteLinesUnified?.count ?? 0}
        defaultSearch={defaultSearch}
        getRowLink={(row) => `../manifest-line-items/${row.id}`}
        onRowClick={(row) => navigate(`../manifest-line-items/${row.id}`)}
        noDataMessage="No manifest line items found."
        commandButtons={({ search }) => [
          <ExportDataButton
            key="export"
            exportType="wasteManifestLines"
            fileName="waste-stream-manifest-line-items"
            tenantId={tenantId}
            filter={{ search }}
            iconOnly
          />,
        ]}
        initialSortModel={[{ field: "shippedDate", sort: "desc" }]}
        rowHeight="auto"
        disableRowSelectionOnClick
        showFilters
        showManageColumns
        initialPageSize={5}
        pageSizeOptions={[5, 10, 25]}
        hideFooter={false}
        columnVisibilityModel={{
          willNotReport: false,
        }}
      />
    </Stack>
  );
};
